'use client';

import { useRouter } from 'next/navigation';
import { createContext, useCallback, useEffect, useState } from 'react';
import { cn } from '@/lib/utils/tw-merge';
import UiModal from '../../UiModal';
import type { UiRouteModalContextValue, UiRouteModalProps } from './UiRouteModal.types';

export const UiRouteModalContext = createContext<UiRouteModalContextValue | null>(null);

/**
 * A page opened as a dialog. Frame, close button and the one scroll area are the
 * design's modal (`styles/components/modal.css`); below md it is a full-screen
 * sheet. It turns Perla for a Perla page inside it, and on a Perla page beneath
 * it (the latter in CSS, so it needs no page data).
 */
function UiRouteModal({ children, appear = true, tone = 'brand', panelClassName, ...props }: UiRouteModalProps) {
    const router = useRouter();
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const id = requestAnimationFrame(() => setOpen(true));
        return () => cancelAnimationFrame(id);
    }, []);

    const handleClose = useCallback(() => {
        setOpen(false);
    }, []);

    const handleClosed = useCallback(() => {
        router.back();
    }, [router]);

    return (
        <UiRouteModalContext.Provider value={{ close: handleClose }}>
            <UiModal
                open={open}
                onClose={handleClose}
                onClosed={handleClosed}
                appear={appear}
                size="xs"
                bare
                backdropClassName="bg-[rgba(var(--viewer-backdrop),0.62)]"
                containerClassName="items-stretch p-0 md:items-center md:p-4"
                panelClassName={cn('route-modal', tone === 'perla' && 'surface-perla', panelClassName)}
                closeButtonClassName="route-modal__close size-11 top-(--modal-close-inset) right-(--modal-close-inset)"
                closeButtonIconClassName="size-5"
                closeButtonLabel="Dialog schließen"
                {...props}
            >
                <div className="route-modal__scroll">{children}</div>
            </UiModal>
        </UiRouteModalContext.Provider>
    );
}

export default UiRouteModal;
